import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  RefreshCw,
  MessageSquare,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Filter,
  Search,
  Send,
  Zap,
  Eye,
  EyeOff,
} from 'lucide-react';
import { API } from '../utils/constants';

// Mapeo status → estilo visual del badge
const STATUS_STYLES = {
  pending:        { label: 'Pendiente',         bg: '#FEF3C7', fg: '#92400E', icon: Clock },
  pending_manual: { label: 'Revisión manual',   bg: '#FFE4E6', fg: '#9F1239', icon: AlertTriangle },
  applied:        { label: 'Aplicado',          bg: '#DCFCE7', fg: '#166534', icon: CheckCircle2 },
  error:          { label: 'Error',             bg: '#FECACA', fg: '#991B1B', icon: AlertTriangle },
};

const KIND_LABELS = {
  section: 'Sección',
  global:  'Global',
};

const PUB_STATUS_LABELS = {
  draft_published:    'Borrador',
  round1_collecting:  'Ronda 1 — comentando',
  round1_correcting:  'Ronda 1 — aplicando',
  round1_done:        'Ronda 1 aplicada',
  round2_collecting:  'Ronda 2 — comentando',
  round2_correcting:  'Ronda 2 — aplicando',
  closed:             'Cerrado',
  approved:           'Aprobado',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (e) {
    return iso;
  }
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = cfg.icon;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.35rem',
        padding: '0.25rem 0.6rem',
        borderRadius: '9999px',
        backgroundColor: cfg.bg,
        color: cfg.fg,
        fontSize: '0.75rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <Icon size={12} />
      {cfg.label}
    </span>
  );
};

const ClientCommentsInbox = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [comments, setComments] = useState([]);
  const [filters, setFilters] = useState({
    status: 'all',
    kind: 'all',
    search: '',
  });
  const [resolveDialog, setResolveDialog] = useState({ open: false, comment: null, note: '' });
  const [resolving, setResolving] = useState(false);
  const [forcingPubId, setForcingPubId] = useState(null);
  // Por defecto ocultamos los comentarios ya aplicados: la bandeja muestra solo
  // lo que requiere atención (pendientes, revisión manual, errores). El operador
  // puede revelarlos con el botón "Mostrar aplicados".
  const [showApplied, setShowApplied] = useState(false);

  useEffect(() => {
    loadInbox();
  }, []);

  const loadInbox = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/portal/admin/inbox`, {
        params: { limit: 500 },
      });
      setComments(data?.comments || []);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Error al cargar los comentarios';
      toast.error(detail);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const filteredComments = useMemo(() => {
    return comments.filter((c) => {
      // Ocultar aplicados por defecto, salvo que el operador los pida (toggle)
      // o esté filtrando explícitamente por status 'applied'.
      if (!showApplied && filters.status !== 'applied' && c.status === 'applied') return false;
      if (filters.status !== 'all' && c.status !== filters.status) return false;
      if (filters.kind !== 'all' && c.kind !== filters.kind) return false;
      if (filters.search) {
        const s = filters.search.toLowerCase();
        const hay = [
          c.applicant_name,
          c.client_name,
          c.case_id,
          c.comment,
          c.section_id,
          c.niw_id,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [comments, filters, showApplied]);

  const counts = useMemo(() => {
    const out = { all: comments.length, pending: 0, pending_manual: 0, applied: 0, error: 0 };
    for (const c of comments) {
      if (out[c.status] !== undefined) out[c.status] += 1;
    }
    return out;
  }, [comments]);

  const openResolveDialog = (comment) => {
    setResolveDialog({ open: true, comment, note: '' });
  };

  const forceApply = async (publicationId, clientLabel) => {
    if (!publicationId) return;
    const ok = window.confirm(
      `¿Aplicar ahora todos los comentarios pendientes de ${clientLabel || 'esta publicación'}?\n\n` +
      `Esto cierra el window de la ronda actual sin esperar y dispara la corrección con LLM inmediatamente. ` +
      `El proceso puede tardar 30s-2min mientras corre el LLM.`
    );
    if (!ok) return;
    setForcingPubId(publicationId);
    try {
      const { data } = await axios.post(`${API}/portal/admin/publications/${publicationId}/force-apply`);
      toast.success(`Ronda ${data.round} aplicada — estado: ${data.new_state}`);
      loadInbox(false);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Error al forzar la aplicación';
      toast.error(detail);
    } finally {
      setForcingPubId(null);
    }
  };

  const submitResolve = async () => {
    const c = resolveDialog.comment;
    if (!c) return;
    setResolving(true);
    try {
      await axios.post(`${API}/portal/admin/comments/${c.id}/resolve`, {
        note: resolveDialog.note || null,
      });
      toast.success('Comentario marcado como resuelto');
      setResolveDialog({ open: false, comment: null, note: '' });
      // Optimistic update local + recarga en background
      setComments((prev) => prev.map((x) => (x.id === c.id ? { ...x, status: 'applied' } : x)));
      loadInbox(false);
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Error al marcar como resuelto';
      toast.error(detail);
    } finally {
      setResolving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '1rem' }}>
        <Loader2 className="animate-spin" size={42} />
        <p style={{ color: '#6B7280' }}>Cargando bandeja…</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem 3rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="mr-2" size={18} />
            Dashboard
          </Button>
          <div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: 0, color: '#111827' }}>
              <MessageSquare className="inline mr-2" size={26} style={{ verticalAlign: 'middle' }} />
              Bandeja de comentarios del cliente
            </h1>
            <p style={{ color: '#6B7280', marginTop: '0.25rem' }}>
              Comentarios recibidos en los portales compartidos. Los <strong>globales</strong> requieren resolución manual.
            </p>
          </div>
        </div>
        <Button onClick={() => loadInbox(false)} disabled={refreshing} variant="outline">
          {refreshing ? <Loader2 className="mr-2 animate-spin" size={16} /> : <RefreshCw className="mr-2" size={16} />}
          Actualizar
        </Button>
      </div>

      {/* Tarjetas de conteo por status */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {[
          { key: 'all',            label: 'Total',           color: '#1F2937' },
          { key: 'pending',        label: 'Pendientes',      color: '#92400E' },
          { key: 'pending_manual', label: 'Revisión manual', color: '#9F1239' },
          { key: 'applied',        label: 'Aplicados',       color: '#166534' },
          { key: 'error',          label: 'Con error',       color: '#991B1B' },
        ].map((s) => (
          <Card
            key={s.key}
            onClick={() => setFilters((f) => ({ ...f, status: s.key }))}
            style={{
              cursor: 'pointer',
              border: filters.status === s.key ? `2px solid ${s.color}` : '1px solid #E5E7EB',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}
          >
            <CardContent style={{ padding: '1rem 1.25rem' }}>
              <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280' }}>
                {s.label}
              </div>
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: s.color, marginTop: '0.25rem' }}>
                {counts[s.key] ?? 0}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filtros */}
      <Card style={{ marginBottom: '1.5rem', border: '1px solid #E5E7EB' }}>
        <CardContent style={{ padding: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
          <Filter size={16} style={{ color: '#6B7280' }} />
          <Select
            value={filters.kind}
            onValueChange={(v) => setFilters((f) => ({ ...f, kind: v }))}
          >
            <SelectTrigger style={{ width: '160px' }}>
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              <SelectItem value="section">Sección</SelectItem>
              <SelectItem value="global">Global</SelectItem>
            </SelectContent>
          </Select>
          <div style={{ flex: 1, minWidth: '240px', position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
            <Input
              placeholder="Buscar por nombre, comentario, sección…"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              style={{ paddingLeft: '32px' }}
            />
          </div>
          <Button
            variant={showApplied ? 'default' : 'outline'}
            onClick={() => setShowApplied((v) => !v)}
            title={showApplied ? 'Ocultar los comentarios ya aplicados' : 'Mostrar también los comentarios ya aplicados'}
          >
            {showApplied
              ? <><EyeOff className="mr-2" size={16} /> Ocultar aplicados</>
              : <><Eye className="mr-2" size={16} /> Mostrar aplicados ({counts.applied})</>}
          </Button>
          {(filters.status !== 'all' || filters.kind !== 'all' || filters.search) && (
            <Button
              variant="ghost"
              onClick={() => setFilters({ status: 'all', kind: 'all', search: '' })}
            >
              Limpiar filtros
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Lista de comentarios */}
      {filteredComments.length === 0 ? (
        <Card style={{ border: '1px dashed #D1D5DB', backgroundColor: '#F9FAFB' }}>
          <CardContent style={{ padding: '3rem', textAlign: 'center' }}>
            <MessageSquare size={42} style={{ color: '#9CA3AF', margin: '0 auto 1rem' }} />
            <p style={{ color: '#6B7280', fontSize: '0.95rem' }}>
              {comments.length === 0
                ? 'Todavía no hay comentarios en ninguna publicación compartida.'
                : (!showApplied && counts.applied > 0)
                  ? `No hay comentarios pendientes de atención. Hay ${counts.applied} ya aplicado(s) oculto(s) — usa "Mostrar aplicados" para revisarlos.`
                  : 'Ningún comentario coincide con los filtros aplicados.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {filteredComments.map((c) => {
            const canResolveManually = c.status === 'pending_manual' || c.status === 'error';
            // "Aplicar ahora" actúa a nivel de PUBLICACIÓN: cierra el window y aplica
            // todos los pendientes de la ronda actual. Solo tiene sentido en un
            // comentario que esté pendiente Y pertenezca a la ronda que está
            // recolectando ahora — no en uno ya aplicado de una ronda anterior.
            const pubRound =
              c.pub_status === 'round1_collecting' ? 1 :
              c.pub_status === 'round2_collecting' ? 2 : null;
            const canForceApply = c.status === 'pending' && pubRound !== null && c.round === pubRound;
            return (
              <Card key={c.id} style={{ border: '1px solid #E5E7EB' }}>
                <CardContent style={{ padding: '1.25rem' }}>
                  {/* Fila superior: cliente + status + ronda */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>
                          {c.client_name || c.applicant_name || 'Cliente sin nombre'}
                        </span>
                        {c.case_id && (
                          <span style={{ fontSize: '0.8rem', color: '#6B7280' }}>
                            · Caso {c.case_id}
                          </span>
                        )}
                        <span
                          style={{
                            fontSize: '0.7rem',
                            padding: '0.15rem 0.5rem',
                            borderRadius: '4px',
                            backgroundColor: c.kind === 'global' ? '#FFE4E6' : '#E0E7FF',
                            color: c.kind === 'global' ? '#9F1239' : '#3730A3',
                            fontWeight: 600,
                          }}
                        >
                          {KIND_LABELS[c.kind] || c.kind}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#6B7280', marginTop: '0.25rem' }}>
                        Ronda {c.round} · {formatDate(c.created_at)} · {(c.lang || 'en').toUpperCase()}
                        {c.section_id && <> · Sección <code style={{ background: '#F3F4F6', padding: '0 0.3rem', borderRadius: '3px' }}>{c.section_id}</code></>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <StatusBadge status={c.status} />
                    </div>
                  </div>

                  {/* Texto del comentario */}
                  <div
                    style={{
                      backgroundColor: '#F9FAFB',
                      borderLeft: '3px solid #D1D5DB',
                      padding: '0.75rem 1rem',
                      fontSize: '0.9rem',
                      color: '#374151',
                      whiteSpace: 'pre-wrap',
                      borderRadius: '0 4px 4px 0',
                      marginBottom: '0.75rem',
                    }}
                  >
                    {c.comment}
                  </div>

                  {/* Error/resolved metadata */}
                  {c.error && (
                    <div style={{ fontSize: '0.8rem', color: '#991B1B', marginBottom: '0.5rem' }}>
                      ⚠️ Error: {c.error}
                    </div>
                  )}
                  {c.resolved_at && (
                    <div style={{ fontSize: '0.8rem', color: '#166534', marginBottom: '0.5rem' }}>
                      ✓ Resuelto el {formatDate(c.resolved_at)}
                      {c.resolved_note && <> — <em>{c.resolved_note}</em></>}
                    </div>
                  )}

                  {/* Acciones */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/view-business-plan/${c.niw_id}`)}
                    >
                      <ExternalLink className="mr-2" size={14} />
                      Abrir NIW
                    </Button>
                    {c.pub_slug && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/p/${c.pub_slug}`, '_blank', 'noopener,noreferrer')}
                      >
                        Ver portal del cliente
                      </Button>
                    )}
                    {canResolveManually && (
                      <Button
                        size="sm"
                        onClick={() => openResolveDialog(c)}
                        style={{ backgroundColor: '#166534', color: 'white' }}
                      >
                        <CheckCircle2 className="mr-2" size={14} />
                        Marcar como resuelto
                      </Button>
                    )}
                    {canForceApply && (
                      <Button
                        size="sm"
                        onClick={() => forceApply(c.publication_id, c.client_name || c.applicant_name)}
                        disabled={forcingPubId === c.publication_id}
                        style={{ backgroundColor: '#7C3AED', color: 'white' }}
                        title="Cierra el window y aplica los comentarios pendientes ahora (sin esperar la hora)"
                      >
                        {forcingPubId === c.publication_id
                          ? <Loader2 className="mr-2 animate-spin" size={14} />
                          : <Zap className="mr-2" size={14} />}
                        Aplicar ahora
                      </Button>
                    )}
                    {c.pub_status && (
                      <span style={{ fontSize: '0.75rem', color: '#6B7280', alignSelf: 'center', marginLeft: 'auto' }}>
                        Estado del portal: <strong>{PUB_STATUS_LABELS[c.pub_status] || c.pub_status}</strong>
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog: marcar como resuelto */}
      <Dialog
        open={resolveDialog.open}
        onOpenChange={(open) => setResolveDialog((d) => ({ ...d, open }))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Marcar comentario como resuelto</DialogTitle>
            <DialogDescription>
              Confirma que ya aplicaste este comentario manualmente al NIW. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          {resolveDialog.comment && (
            <div style={{ backgroundColor: '#F9FAFB', padding: '0.75rem', borderRadius: '6px', fontSize: '0.85rem', color: '#374151' }}>
              "{resolveDialog.comment.comment.slice(0, 200)}{resolveDialog.comment.comment.length > 200 ? '…' : ''}"
            </div>
          )}
          <div>
            <label style={{ fontSize: '0.85rem', fontWeight: 500, color: '#374151', display: 'block', marginBottom: '0.4rem' }}>
              Nota interna (opcional)
            </label>
            <Input
              placeholder="Ej. Aplicado a §3 con ai-edit"
              value={resolveDialog.note}
              onChange={(e) => setResolveDialog((d) => ({ ...d, note: e.target.value }))}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialog({ open: false, comment: null, note: '' })}>
              Cancelar
            </Button>
            <Button onClick={submitResolve} disabled={resolving} style={{ backgroundColor: '#166534', color: 'white' }}>
              {resolving ? <Loader2 className="mr-2 animate-spin" size={14} /> : <Send className="mr-2" size={14} />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientCommentsInbox;
